import { Effect } from "effect";
import { ContractsMarketAssetState } from "./types";
import { getViemMarketAssetState } from "./get-viem-market-asset-state";
import {
  Address,
  chains,
  getChainKeyFromProtocol,
  PERCENT_ZERO,
  ProtocolKeyService,
  ProtocolStateService,
} from "@silo/types";
import {
  calculateRewardsApr,
  convertToUsd,
  getCollateralBaseApr,
} from "@silo/utils";
import { getProtocolConfig } from "@silo/config";
import { getRewardsEmissions } from "../get-rewards-emissions";

export type GetMarketAssetState = ReturnType<typeof getMarketAssetState>;
export type GetMarketAssetStateSuccess =
  Effect.Effect.Success<GetMarketAssetState>;
export type GetMarketAssetStateError = Effect.Effect.Error<GetMarketAssetState>;
export type GetMarketAssetStateContext =
  Effect.Effect.Context<GetMarketAssetState>;

interface GetMarketAssetStateParams {
  marketAddress: Address;
  tokenAddress: Address;
  decimals: number;
  collateralShareToken: Address;
  protectedShareToken: Address;
  debtShareToken: Address;
}

export function getMarketAssetState(params: GetMarketAssetStateParams) {
  return Effect.gen(function* () {
    const { decimals } = params;

    const [
      {
        priceNative,
        liquidity,
        utilization,
        collateralTotal,
        rawCollateralBaseApr,
        protectedTotal,
        debtTotal,
        debtBaseApr,
        isPaused,
      },
      {
        collateralEmissionPerSecond,
        protectedEmissionPerSecond,
        debtEmissionPerSecond,
      },
    ] = yield* Effect.all(
      [getViemMarketAssetState(params), getRewardsEmissions(params)],
      {
        concurrency: "unbounded",
      }
    );

    const { protocolShareFee, rewardTokenPriceUsd, quotePriceUsd } =
      yield* ProtocolStateService;

    const collateralBaseApr = getCollateralBaseApr(
      protocolShareFee,
      rawCollateralBaseApr
    );

    const protocolKey = yield* ProtocolKeyService;
    const chainKey = getChainKeyFromProtocol(protocolKey);
    const { nativeCurrency } = chains[chainKey];

    const priceUsd = convertToUsd(
      priceNative,
      nativeCurrency.decimals,
      quotePriceUsd
    );

    const { nativeRewards } = getProtocolConfig(protocolKey);

    const collateralRewardsApr = nativeRewards
      ? calculateRewardsApr({
          emissionPerSecond: collateralEmissionPerSecond,
          rewardTokenPriceUsd,
          rewardTokenDecimals: nativeRewards.decimals,
          totalTokens: collateralTotal,
          tokenPriceUsd: priceUsd,
          tokenDecimals: decimals,
        })
      : PERCENT_ZERO;

    const protectedRewardsApr = nativeRewards
      ? calculateRewardsApr({
          emissionPerSecond: protectedEmissionPerSecond,
          rewardTokenPriceUsd,
          rewardTokenDecimals: nativeRewards.decimals,
          totalTokens: protectedTotal,
          tokenPriceUsd: priceUsd,
          tokenDecimals: decimals,
        })
      : PERCENT_ZERO;

    const debtRewardsApr = nativeRewards
      ? calculateRewardsApr({
          emissionPerSecond: debtEmissionPerSecond,
          rewardTokenPriceUsd,
          rewardTokenDecimals: nativeRewards.decimals,
          totalTokens: debtTotal,
          tokenPriceUsd: priceUsd,
          tokenDecimals: decimals,
        })
      : PERCENT_ZERO;

    const contractsMarketAssetState: ContractsMarketAssetState = {
      priceNative,
      priceUsd,

      liquidity,
      utilization,

      collateralTotal,
      collateralBaseApr,
      collateralRewardsApr,
      collateralEmissionPerSecond,

      protectedTotal,
      protectedRewardsApr,
      protectedEmissionPerSecond,

      debtTotal,
      debtBaseApr,
      debtRewardsApr,
      debtEmissionPerSecond,

      isPaused,
    };

    return contractsMarketAssetState;
  });
}
